import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuote } from '../contexts/QuoteContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Send, User, Mail, MapPin, Package, Check, Loader2, Building2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => `£${(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

const StepIndicator = ({ currentStep }) => (
    <div className="flex items-center justify-center gap-2 mb-6">
        {[1, 2, 3].map((step) => (
            <div key={step} className={`h-2 rounded-full transition-all duration-300 ${step === currentStep ? 'w-8 bg-brand-lime' : step < currentStep ? 'w-2 bg-brand-lime' : 'w-2 bg-brand-gray-200'}`} />
        ))}
    </div>
);

const ReviewQuote = () => {
    const navigate = useNavigate();
    const { user, token } = useAuth();
    const { customerDetails, selectedItems, setCreatedQuote } = useQuote();
    const [loading, setLoading] = useState(false);
    const [vatRates, setVatRates] = useState([]);
    const [duplicateWarning, setDuplicateWarning] = useState(null);

    useEffect(() => {
        if (!customerDetails.company_name || !customerDetails.email || selectedItems.length === 0) {
            navigate('/');
            return;
        }
        fetchVatRates();
    }, [customerDetails, selectedItems, navigate]);

    const fetchVatRates = async () => {
        try {
            const res = await axios.get(`${API}/vat-rates`, { headers: { Authorization: `Bearer ${token}` } });
            setVatRates(res.data);
        } catch { /* ignore */ }
    };

    // Compute line-by-line pricing
    const lineItems = selectedItems.map(({ pack, quantity }) => {
        const vatRecord = vatRates.find(r => r.id === pack.vat_rate_id);
        const rate = vatRecord ? vatRecord.rate : 0;
        const rateName = vatRecord ? vatRecord.name : 'No VAT';
        const lineEx = pack.price * quantity;
        const lineVat = Math.round(lineEx * rate / 100 * 100) / 100;
        return {
            pack, quantity, unitPrice: pack.price,
            vatRate: rate, vatName: rateName,
            lineExVat: lineEx, lineVat, lineIncVat: Math.round((lineEx + lineVat) * 100) / 100
        };
    });

    const totalExVat = lineItems.reduce((s, i) => s + i.lineExVat, 0);
    const totalVat = lineItems.reduce((s, i) => s + i.lineVat, 0);
    const totalIncVat = Math.round((totalExVat + totalVat) * 100) / 100;

    // Group VAT for summary
    const vatSummary = {};
    lineItems.forEach(i => {
        const key = `${i.vatName} ${i.vatRate}%`;
        vatSummary[key] = (vatSummary[key] || 0) + i.lineVat;
    });

    const submitQuote = async (force = false) => {
        setLoading(true);
        try {
            const response = await axios.post(`${API}/quotes`, {
                customer: {
                    company_name: customerDetails.company_name,
                    contact_name: customerDetails.contact_name,
                    contact_job_title: customerDetails.contact_job_title || '',
                    email: customerDetails.email,
                    address: customerDetails.address
                },
                items: selectedItems.map(({ pack, quantity }) => ({
                    pack_id: pack.id,
                    quantity
                })),
                force
            }, { headers: { Authorization: `Bearer ${token}` } });
            setCreatedQuote(response.data);
            toast.success('Quote sent successfully!');
            navigate('/success');
        } catch (error) {
            if (error.response?.status === 409) {
                const detail = error.response.data?.detail;
                setDuplicateWarning(typeof detail === 'object' ? detail : { message: detail, existing_ref: '' });
            } else {
                toast.error(error.response?.data?.detail || 'Failed to send quote');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSendQuote = () => submitQuote(false);
    const handleForceQuote = () => { setDuplicateWarning(null); submitQuote(true); };

    if (selectedItems.length === 0) return null;

    return (
        <div className="max-w-lg mx-auto animate-fade-in">
            <StepIndicator currentStep={3} />
            <Card className="shadow-lg border-0">
                <CardHeader>
                    <CardTitle className="text-2xl font-bold font-heading">Review & Send</CardTitle>
                    <CardDescription>Review the quote details before sending</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Quote Receipt */}
                    <div className="quote-receipt" data-testid="quote-receipt">
                        <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/20">
                            <div>
                                <span className="text-xs uppercase tracking-wider text-white/60">Quote For</span>
                                <h3 className="text-lg font-bold font-heading text-white">{customerDetails.company_name}</h3>
                                <p className="text-sm text-white/80">
                                    {customerDetails.contact_name}
                                    {customerDetails.contact_job_title && ` (${customerDetails.contact_job_title})`}
                                </p>
                            </div>
                            <div className="text-right">
                                <span className="text-xs uppercase tracking-wider text-white/60">Date</span>
                                <p className="text-white font-medium">{new Date().toLocaleDateString('en-GB')}</p>
                            </div>
                        </div>

                        {/* Line items */}
                        <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/60 mb-1">
                                <Package className="h-4 w-4 text-brand-lime" />
                                {lineItems.length} Pack{lineItems.length > 1 ? 's' : ''}
                            </div>
                            {lineItems.map((item, idx) => (
                                <div key={item.pack.id} className="flex items-center justify-between gap-2 text-sm bg-white/5 rounded px-3 py-2" data-testid={`line-item-${idx}`}>
                                    <div className="flex-1 min-w-0">
                                        <span className="text-white font-medium line-clamp-1">{item.pack.name}</span>
                                        <span className="text-white/50 ml-1">x{item.quantity}</span>
                                    </div>
                                    <span className="text-white font-medium whitespace-nowrap">{fmt(item.lineExVat)}</span>
                                </div>
                            ))}
                        </div>

                        {/* Totals */}
                        <div className="pt-4 border-t border-white/20 space-y-2">
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-white/70">Subtotal (Ex VAT)</span>
                                <span className="text-white font-medium">{fmt(totalExVat)}</span>
                            </div>
                            {Object.entries(vatSummary).map(([label, amount]) => (
                                <div key={label} className="flex justify-between items-center text-sm">
                                    <span className="text-white/70">VAT ({label})</span>
                                    <span className="text-white font-medium">{fmt(amount)}</span>
                                </div>
                            ))}
                            <div className="flex justify-between items-center pt-2 border-t border-white/20">
                                <span className="text-lg font-bold font-heading text-white/80">Total (Inc VAT)</span>
                                <span className="text-3xl font-bold font-heading text-brand-lime" data-testid="total-inc-vat">
                                    {fmt(totalIncVat)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Customer Details Summary */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase tracking-wide text-brand-gray-500">Customer Details</h4>
                        <div className="space-y-2">
                            <div className="flex items-center gap-3 text-sm"><Building2 className="h-4 w-4 text-brand-gray-400" /><span className="text-brand-gray-600">{customerDetails.company_name}</span></div>
                            <div className="flex items-center gap-3 text-sm">
                                <User className="h-4 w-4 text-brand-gray-400" />
                                <span className="text-brand-gray-600">
                                    {customerDetails.contact_name}
                                    {customerDetails.contact_job_title && <span className="text-brand-gray-400 ml-1">({customerDetails.contact_job_title})</span>}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-sm"><Mail className="h-4 w-4 text-brand-gray-400" /><span className="text-brand-gray-600">{customerDetails.email}</span></div>
                            <div className="flex items-start gap-3 text-sm"><MapPin className="h-4 w-4 text-brand-gray-400 flex-shrink-0 mt-0.5" /><span className="text-brand-gray-600">{customerDetails.address}</span></div>
                        </div>
                    </div>

                    {/* Engineer */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase tracking-wide text-brand-gray-500">Prepared By</h4>
                        <div className="flex items-center gap-3 p-3 bg-brand-gray-50 rounded-lg">
                            <div className="h-10 w-10 rounded-full bg-brand-lime flex items-center justify-center"><User className="h-5 w-5 text-black" /></div>
                            <div>
                                <p className="font-medium text-brand-charcoal">{user?.name}</p>
                                <p className="text-sm text-brand-gray-500">{user?.email}</p>
                            </div>
                        </div>
                    </div>

                    {/* What happens next */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <h4 className="text-sm font-bold text-blue-800 mb-2">What happens next?</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li className="flex items-center gap-2"><Check className="h-4 w-4" /> PDF quote generated with branding</li>
                            <li className="flex items-center gap-2"><Check className="h-4 w-4" /> Email sent to customer</li>
                            <li className="flex items-center gap-2"><Check className="h-4 w-4" /> Copy sent to sales team</li>
                        </ul>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <Button type="button" variant="outline" onClick={() => navigate('/select-pack')} className="flex-1 h-12" disabled={loading} data-testid="back-to-packs-btn">
                            <ArrowLeft className="h-5 w-5 mr-2" /> Back
                        </Button>
                        <Button type="button" onClick={handleSendQuote} disabled={loading} className="flex-1 h-12 bg-[#7AC943] hover:bg-[#68B035] text-black font-bold uppercase tracking-wide" data-testid="send-quote-btn">
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-5 w-5 mr-2" /> Send Quote</>}
                        </Button>
                    </div>

                    {/* Duplicate Warning Dialog */}
                    <AlertDialog open={!!duplicateWarning} onOpenChange={() => setDuplicateWarning(null)}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle className="flex items-center gap-2">
                                    <AlertTriangle className="h-5 w-5 text-amber-500" /> Existing Quote Found
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-left">
                                    {duplicateWarning?.message || 'An active quote already exists for this customer.'}
                                    <br /><br />
                                    Would you like to send a new quote anyway?
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="cancel-duplicate-btn">Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={handleForceQuote} className="bg-[#7AC943] hover:bg-[#68B035] text-black font-bold" data-testid="continue-duplicate-btn">
                                    Send Anyway
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </CardContent>
            </Card>
        </div>
    );
};

export default ReviewQuote;
